import { ForwardedRef, HTMLAttributes, MouseEvent, ReactNode, forwardRef } from 'react';
import styled, { FlattenSimpleInterpolation, css } from 'styled-components';
import { CircleSpinner } from 'ui/spinner';
import { spacing } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import { pxToRem } from 'utils/convert';
import noop from 'utils/noop';
import { BaseButton } from './Styled';
import { BUTTON_SIZES, BUTTON_VARIANTS } from './constants';

export type IconButtonProps = {
  autoFocus?: boolean;
  /** An override to disable the button */
  isDisabled?: boolean;
  /** The click handler for the button */
  onClick?: (e: MouseEvent) => void;
  /** The size variant of the button */
  size?: BUTTON_SIZES;
  /** The type variant of the button */
  variant?: BUTTON_VARIANTS;
  children?: ReactNode;
  ref?: React.Ref<HTMLButtonElement> | null;
  isLoading?: boolean;
  /** Highly encouraged to add descriptive text for icon button displayed in a tooltip */
  title?: string;
  type?: 'button' | 'submit' | 'reset';
} & HTMLAttributes<HTMLButtonElement>;

export const SIZE_STYLES: Record<BUTTON_SIZES, FlattenSimpleInterpolation> = {
  [BUTTON_SIZES.LARGE]: css({
    height: pxToRem(40),
    width: pxToRem(40),
  }),
  [BUTTON_SIZES.MEDIUM]: css({
    height: pxToRem(36),
    width: pxToRem(36),
  }),
  [BUTTON_SIZES.SMALL]: css({
    height: pxToRem(32),
    width: pxToRem(32),
  }),
};

export const BaseIconButton = styled(BaseButton)`
  padding: 0 ${spacing.half};
  ${({ size }) => SIZE_STYLES[size]};
  :not([disabled]) {
    cursor: pointer;
  }
`;

export const IconButton = forwardRef(
  (
    {
      autoFocus,
      isDisabled = false,
      onClick = noop,
      size = BUTTON_SIZES.MEDIUM,
      title,
      variant = BUTTON_VARIANTS.PRIMARY,
      isLoading,
      children,
      ...props
    }: IconButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => (
    <Tooltip
      automaticallyAvoidCollisions
      delayDuration={1000}
      isEnabled={!!title}
      isUsedOnButton
      tooltipContent={title}
    >
      <BaseIconButton
        autoFocus={autoFocus}
        aria-disabled={isDisabled}
        aria-label={title}
        data-test={
          isDisabled ? `${size}-${variant}-icon-button-disabled` : `${size}-${variant}-icon-button`
        }
        onClick={onClick}
        ref={ref}
        size={size}
        variant={variant}
        title={undefined}
        {...props}
      >
        {isLoading ? (
          <>
            <CircleSpinner size={size} isColorInherited />
          </>
        ) : (
          children
        )}
      </BaseIconButton>
    </Tooltip>
  ),
);
